import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  type GetDataRoomNodesQueryResult,
  getGetDataRoomNodesQueryKey,
  usePutDataRoomNodes,
} from "@vega-api/generated/coinvest-deal-data-room-nodes/coinvest-deal-data-room-nodes";
import type { DataRoomDealOpportunity } from "@vega-api/generated/types/dataRoomDealOpportunity";
import type { DataRoomFile } from "@vega-api/generated/types/dataRoomFile";
import type { DataRoomFileTreeVisibleRow } from "@vega-modules/scenes/CoInvestments/components/DataRoomFileTree/types";
import { useVisibleDataRoomRows } from "@vega-modules/scenes/CoInvestments/components/DataRoomFileTree/useVisibleDataRoomRows";
import { isViewableInApp } from "@vega-modules/scenes/CoInvestments/utils/dataRoomUtils";

import type {
  AccessMap,
  DownloadableMap,
  NodeMap,
  ParsedDataRoom,
  PermissionChange,
  TreeNode,
  TriState,
} from "../components/DataRoomPermissions/types";
import type { MergeTarget } from "../components/DataRoomPermissions/utils/dropUtils";
import {
  compareNodes,
  computeDiff,
  getDescendants,
  getRootIds,
  serializeNodes,
  triState as computeTriState,
} from "../components/DataRoomPermissions/utils/treeUtils";

type VisibleRow = DataRoomFileTreeVisibleRow<TreeNode>;

type ReplacedFileSnapshot = {
  originalFile?: DataRoomFile;
  originalCreatedAt: string | null;
};

type UseDataRoomPermissionsReturn = {
  nodeMap: NodeMap;
  access: AccessMap;
  downloadable: DownloadableMap;
  rootIds: string[];
  visibleRows: VisibleRow[];
  allFileIds: string[];
  diff: PermissionChange[];
  changeCount: number;
  newNodeCount: { folders: number; files: number; total: number };
  replacedIds: Set<string>;
  collapsed: Set<string>;
  toggleCollapse: (id: string) => void;
  handleDiscard: () => void;
  handleSave: () => Promise<void>;
  isSaving: boolean;
  setFileDealOpportunityAccess: (
    fileId: string,
    dealOpportunityId: string,
    val: boolean,
  ) => void;
  toggleFolderDealOpportunityAccess: (
    nodeId: string,
    dealOpportunityId: string,
  ) => void;
  toggleDealOpportunityColumnAccess: (dealOpportunityId: string) => void;
  toggleFileRowAccess: (fileId: string) => void;
  toggleFolderRowAccess: (folderId: string) => void;
  toggleDownloadable: (nodeId: string) => void;
  isDownloadable: (nodeId: string) => boolean;
  createFolder: (name: string, parentId: string | null) => void;
  addNodes: (newNodes: TreeNode[], mergeTargets?: MergeTarget[]) => void;
  removeNode: (nodeId: string) => void;
  setNodeFile: (nodeId: string, file: DataRoomFile) => void;
  replaceNodeFile: (nodeId: string, file: DataRoomFile) => void;
  deleteNode: (nodeId: string) => void;
  deletedIds: Set<string>;
  getTriStateForFileRow: (fileId: string) => TriState;
  getTriStateForFolderRow: (folderId: string) => TriState;
  getTriStateForFolderDealOpportunity: (
    folderId: string,
    dealOpportunityId: string,
  ) => TriState;
  getTriStateForDealOpportunityColumn: (dealOpportunityId: string) => TriState;
};

export function useDataRoomPermissions(
  dealId: string,
  dataRoomId: string | undefined,
  parsedData: ParsedDataRoom,
  dealOpportunities: DataRoomDealOpportunity[],
): UseDataRoomPermissionsReturn {
  const queryClient = useQueryClient();
  const { mutateAsync: putDataRoomNodes, isPending: isSaving } =
    usePutDataRoomNodes();
  const [nodeMap, setNodeMap] = useState<NodeMap>(() => parsedData.nodeMap);
  const [savedAccess, setSavedAccess] = useState<AccessMap>(
    () => parsedData.access,
  );
  const [access, setAccess] = useState<AccessMap>(() => parsedData.access);
  const [savedDownloadable, setSavedDownloadable] = useState<DownloadableMap>(
    () => parsedData.downloadable,
  );
  const [downloadable, setDownloadable] = useState<DownloadableMap>(
    () => parsedData.downloadable,
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(() => new Set());
  const [replacedFiles, setReplacedFiles] = useState<
    Map<string, ReplacedFileSnapshot>
  >(() => new Map());

  // Sync state when API data arrives (useState initializers only run on mount)
  useEffect(() => {
    setNodeMap(parsedData.nodeMap);
    setSavedAccess(parsedData.access);
    setAccess(parsedData.access);
    setSavedDownloadable(parsedData.downloadable);
    setDownloadable(parsedData.downloadable);
    setCollapsed(new Set());
    setDeletedIds(new Set());
    setReplacedFiles(new Map());
  }, [parsedData]);

  const rootIds = useMemo(() => getRootIds(nodeMap), [nodeMap]);
  const allFileIds = useMemo(
    () => Object.keys(access).filter((id) => !deletedIds.has(id)),
    [access, deletedIds],
  );

  // Precompute descendant files per folder — avoids repeated tree walks in render
  const descendantFilesMap = useMemo(() => {
    const map = new Map<string, string[]>();

    for (const node of Object.values(nodeMap)) {
      if (node?.type === "folder") {
        map.set(
          node.id,
          getDescendants(node.id, nodeMap).filter(
            (id) => nodeMap[id]?.type === "file",
          ),
        );
      }
    }

    return map;
  }, [nodeMap]);

  const diff = useMemo(
    () =>
      computeDiff(
        savedAccess,
        access,
        savedDownloadable,
        downloadable,
        nodeMap,
      ),
    [savedAccess, access, savedDownloadable, downloadable, nodeMap],
  );

  const replacedIds = useMemo(
    () => new Set(replacedFiles.keys()),
    [replacedFiles],
  );

  const newNodeCount = useMemo(() => {
    let folders = 0;
    let files = 0;

    for (const n of Object.values(nodeMap)) {
      if (!n?.isNew) continue;

      if (n.type === "folder") folders++;
      else files++;
    }

    return { folders, files, total: folders + files };
  }, [nodeMap]);

  const visibleRows = useVisibleDataRoomRows({
    nodeMap,
    rootIds,
    collapsedIds: collapsed,
    hiddenIds: deletedIds,
  });

  function toggleCollapse(id: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }

  function handleDiscard() {
    setNodeMap((prev) => {
      const next: NodeMap = {};

      for (const [id, node] of Object.entries(prev)) {
        if (!node || node.isNew) continue;

        const snapshot = replacedFiles.get(id);
        const restored = snapshot
          ? {
              ...node,
              file: snapshot.originalFile,
              createdAt: snapshot.originalCreatedAt,
            }
          : node;

        next[id] = {
          ...restored,
          children: restored.children.filter((c) => !prev[c]?.isNew),
        };
      }

      return next;
    });
    setAccess(structuredClone(savedAccess));
    setDownloadable({ ...savedDownloadable });
    setDeletedIds(new Set());
    setReplacedFiles(new Map());
  }

  async function handleSave() {
    if (!dataRoomId) return;

    const nodes = serializeNodes(nodeMap, access, downloadable, deletedIds);
    const updatedDataRoom = await putDataRoomNodes({
      dealId,
      dataRoomId,
      data: { nodes },
    });
    setSavedAccess(structuredClone(access));
    setSavedDownloadable({ ...downloadable });
    setReplacedFiles(new Map());
    queryClient.setQueryData<GetDataRoomNodesQueryResult>(
      getGetDataRoomNodesQueryKey(dealId, dataRoomId),
      updatedDataRoom,
    );
  }

  function setFileDealOpportunityAccess(
    fileId: string,
    dealOpportunityId: string,
    val: boolean,
  ) {
    setAccess((prev) => ({
      ...prev,
      [fileId]: {
        ...prev[fileId],
        [dealOpportunityId]: val,
      },
    }));
  }

  function toggleFolderDealOpportunityAccess(
    nodeId: string,
    dealOpportunityId: string,
  ) {
    const triState = getTriStateForFolderDealOpportunity(
      nodeId,
      dealOpportunityId,
    );
    const newVal = triState !== true;
    const files = descendantFilesMap.get(nodeId) ?? [];

    setAccess((prev) => {
      const next = { ...prev };

      files.forEach((fileId) => {
        next[fileId] = { ...prev[fileId], [dealOpportunityId]: newVal };
      });

      return next;
    });
  }

  function isDownloadable(nodeId: string): boolean {
    const node = nodeMap[nodeId];

    if (!node) return false;

    if (node.type === "file") {
      return !!downloadable[nodeId];
    }

    const viewableFiles = (descendantFilesMap.get(nodeId) ?? []).filter(
      (id) => {
        const n = nodeMap[id];

        return n !== undefined && isViewableInApp(n);
      },
    );

    return (
      viewableFiles.length > 0 &&
      viewableFiles.every((fileId) => !!downloadable[fileId])
    );
  }

  function toggleDownloadable(nodeId: string) {
    const node = nodeMap[nodeId];

    if (!node) return;

    if (node.type === "file") {
      if (!isViewableInApp(node)) return;

      setDownloadable((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));

      return;
    }

    const files = descendantFilesMap.get(nodeId) ?? [];
    const viewableFiles = files.filter((id) => {
      const n = nodeMap[id];

      return n !== undefined && isViewableInApp(n);
    });
    const newVal =
      computeTriState(viewableFiles.map((id) => !!downloadable[id])) !== true;

    setDownloadable((prev) => {
      const next = { ...prev };

      viewableFiles.forEach((fileId) => {
        next[fileId] = newVal;
      });

      return next;
    });
  }

  function createFolder(name: string, parentId: string | null) {
    const id = crypto.randomUUID();
    const parentPath = parentId ? (nodeMap[parentId]?.path ?? "") : "";
    const newFolder: TreeNode = {
      id,
      name,
      type: "folder",
      parentId,
      children: [],
      createdAt: null,
      path: `${parentPath}/${name}`,
      isNew: true,
    };

    setNodeMap((prev) => {
      const next = { ...prev, [id]: newFolder };

      if (parentId) {
        const parent = prev[parentId];

        if (parent) {
          const children = [...parent.children, id].sort((a, b) => {
            const nodeA = next[a];
            const nodeB = next[b];

            if (!nodeA || !nodeB) return 0;

            return compareNodes(nodeA, nodeB);
          });
          next[parentId] = { ...parent, children };
        }
      }

      return next;
    });

    if (parentId) {
      setCollapsed((prev) => {
        const next = new Set(prev);
        next.delete(parentId);

        return next;
      });
    }
  }

  function getTriStateForFileRow(fileId: string): TriState {
    const vals = dealOpportunities.map(
      (dealOpportunity) => !!access[fileId]?.[dealOpportunity.id],
    );

    return computeTriState(vals);
  }

  function getTriStateForFolderDealOpportunity(
    folderId: string,
    dealOpportunityId: string,
  ): TriState {
    const files = descendantFilesMap.get(folderId) ?? [];
    const vals = files.map((fileId) => !!access[fileId]?.[dealOpportunityId]);

    return computeTriState(vals);
  }

  function toggleDealOpportunityColumnAccess(dealOpportunityId: string) {
    const triState = getTriStateForDealOpportunityColumn(dealOpportunityId);
    const newVal = triState !== true;

    setAccess((prev) => {
      const next = { ...prev };

      allFileIds.forEach((fileId) => {
        next[fileId] = { ...prev[fileId], [dealOpportunityId]: newVal };
      });

      return next;
    });
  }

  function allDealOpportunitiesAccessMap(
    val: boolean,
  ): Record<string, boolean> {
    return Object.fromEntries(
      dealOpportunities.map((dealOpportunity) => [dealOpportunity.id, val]),
    );
  }

  function toggleFileRowAccess(fileId: string) {
    const newVal = getTriStateForFileRow(fileId) !== true;

    setAccess((prev) => ({
      ...prev,
      [fileId]: allDealOpportunitiesAccessMap(newVal),
    }));
  }

  function getTriStateForFolderRow(folderId: string): TriState {
    const files = descendantFilesMap.get(folderId) ?? [];
    const vals = files.flatMap((fileId) =>
      dealOpportunities.map(
        (dealOpportunity) => !!access[fileId]?.[dealOpportunity.id],
      ),
    );

    return computeTriState(vals);
  }

  function toggleFolderRowAccess(folderId: string) {
    const newVal = getTriStateForFolderRow(folderId) !== true;
    const files = descendantFilesMap.get(folderId) ?? [];
    const accessForAllDealOpportunities = allDealOpportunitiesAccessMap(newVal);

    setAccess((prev) => {
      const next = { ...prev };

      files.forEach((fileId) => {
        next[fileId] = accessForAllDealOpportunities;
      });

      return next;
    });
  }

  function getTriStateForDealOpportunityColumn(
    dealOpportunityId: string,
  ): TriState {
    const vals = allFileIds.map(
      (fileId) => !!access[fileId]?.[dealOpportunityId],
    );

    return computeTriState(vals);
  }

  function addNodes(newNodes: TreeNode[], mergeTargets: MergeTarget[] = []) {
    setNodeMap((prev) => {
      const next = { ...prev };

      for (const node of newNodes) {
        next[node.id] = node;
      }

      const sortChildren = (children: string[]) =>
        [...children].sort((a, b) => {
          const nodeA = next[a];
          const nodeB = next[b];

          if (!nodeA || !nodeB) return 0;

          return compareNodes(nodeA, nodeB);
        });

      // Update parent children arrays for nodes whose parent already existed
      for (const node of newNodes) {
        if (node.parentId && next[node.parentId]) {
          const parent = next[node.parentId]!;

          if (!parent.children.includes(node.id)) {
            next[node.parentId] = {
              ...parent,
              children: sortChildren([...parent.children, node.id]),
            };
          }
        }
      }

      // Merge new children into existing folders (immutably)
      for (const { existingFolderId, newChildIds } of mergeTargets) {
        const folder = next[existingFolderId];

        if (!folder) continue;

        const existingChildren = new Set(folder.children);
        const additions = newChildIds.filter((id) => !existingChildren.has(id));

        if (additions.length > 0) {
          next[existingFolderId] = {
            ...folder,
            children: sortChildren([...folder.children, ...additions]),
          };
        }
      }

      return next;
    });

    // Initialize access and downloadable for new file nodes
    setAccess((prev) => {
      const next = { ...prev };

      for (const node of newNodes) {
        if (node.type === "file" && !next[node.id]) {
          next[node.id] = {};
        }
      }

      return next;
    });

    setDownloadable((prev) => {
      const next = { ...prev };

      for (const node of newNodes) {
        if (node.type === "file") {
          next[node.id] = !isViewableInApp(node);
        }
      }

      return next;
    });

    // Auto-expand parent folders that received new children
    const parentIds = new Set(
      newNodes.map((n) => n.parentId).filter((id): id is string => id !== null),
    );

    setCollapsed((prev) => {
      const next = new Set(prev);

      for (const pid of parentIds) {
        next.delete(pid);
      }

      return next;
    });
  }

  function removeNode(nodeId: string) {
    const node = nodeMap[nodeId];

    if (!node) return;

    // Collect all IDs to remove (node + descendants) before mutating state
    const toRemove = new Set<string>();

    function collectDescendants(id: string) {
      toRemove.add(id);
      const n = nodeMap[id];

      if (n?.type === "folder") {
        n.children.forEach(collectDescendants);
      }
    }

    collectDescendants(nodeId);

    setNodeMap((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => !toRemove.has(id)),
      ) as NodeMap;

      if (node.parentId && next[node.parentId]) {
        const parent = next[node.parentId]!;

        next[node.parentId] = {
          ...parent,
          children: parent.children.filter((cid) => cid !== nodeId),
        };
      }

      return next;
    });

    setAccess((prev) => {
      const idsToRemove = new Set(
        [...toRemove].filter((id) => Object.hasOwn(prev, id)),
      );

      if (idsToRemove.size === 0) return prev;

      return Object.fromEntries(
        Object.entries(prev).filter(([id]) => !idsToRemove.has(id)),
      ) as AccessMap;
    });

    setDownloadable((prev) => {
      const idsToRemove = new Set(
        [...toRemove].filter((id) => Object.hasOwn(prev, id)),
      );

      if (idsToRemove.size === 0) return prev;

      return Object.fromEntries(
        Object.entries(prev).filter(([id]) => !idsToRemove.has(id)),
      ) as DownloadableMap;
    });
  }

  function deleteNode(nodeId: string) {
    const node = nodeMap[nodeId];

    if (!node) return;

    // New nodes were never saved — just remove from nodeMap
    if (node.isNew) {
      removeNode(nodeId);

      return;
    }

    // Existing nodes: track as deleted (soft delete, restorable via discard)
    const idsToDelete = getDescendants(nodeId, nodeMap);

    setDeletedIds((prev) => {
      const next = new Set(prev);

      idsToDelete.forEach((id) => next.add(id));

      return next;
    });
  }

  function setNodeFile(nodeId: string, file: DataRoomFile) {
    setNodeMap((prev) => {
      const node = prev[nodeId];

      if (!node) return prev;

      return { ...prev, [nodeId]: { ...node, file } };
    });
  }

  function replaceNodeFile(nodeId: string, file: DataRoomFile) {
    const node = nodeMap[nodeId];

    if (!node) return;

    // For nodes that haven't been saved yet, just swap the file — no
    // saved-state to revert and no need to count as a "replacement".
    if (node.isNew) {
      setNodeFile(nodeId, file);

      return;
    }

    setReplacedFiles((prev) => {
      if (prev.has(nodeId)) return prev;

      const next = new Map(prev);
      next.set(nodeId, {
        originalFile: node.file,
        originalCreatedAt: node.createdAt,
      });

      return next;
    });

    setNodeMap((prev) => {
      const target = prev[nodeId];

      if (!target) return prev;

      return {
        ...prev,
        [nodeId]: {
          ...target,
          file,
          createdAt: new Date().toISOString(),
        },
      };
    });
  }

  return {
    nodeMap,
    access,
    downloadable,
    rootIds,
    visibleRows,
    allFileIds,
    diff,
    changeCount:
      diff.length + newNodeCount.total + deletedIds.size + replacedFiles.size,
    newNodeCount,
    replacedIds,
    collapsed,
    toggleCollapse,
    handleDiscard,
    handleSave,
    isSaving,
    setFileDealOpportunityAccess,
    toggleFolderDealOpportunityAccess,
    toggleDealOpportunityColumnAccess,
    toggleFileRowAccess,
    toggleFolderRowAccess,
    toggleDownloadable,
    isDownloadable,
    createFolder,
    addNodes,
    removeNode,
    setNodeFile,
    replaceNodeFile,
    deleteNode,
    deletedIds,
    getTriStateForFileRow,
    getTriStateForFolderRow,
    getTriStateForFolderDealOpportunity,
    getTriStateForDealOpportunityColumn,
  };
}
